import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  format, 
  addDays, 
  compareAsc,
  differenceInDays, 
  isSameDay,
} from "date-fns";
import toast from 'react-hot-toast';
import { DndContext, closestCenter } from "@dnd-kit/core";
import { 
  SortableContext, 
  arrayMove, 
  verticalListSortingStrategy 
} from "@dnd-kit/sortable";
import {
  useTheme, 
  useMediaQuery, 
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import StyledButton from 'components/StyledButton';
import { 
  LAYOUT_HEIGHT,
  OTP_DELAY_MINUTES,
  MAXIMUM_TAT_MINUTES,
  DIVERT_RETURN_STATUS,
  EXCLUDED_FLIGHT_STATUS,
} from 'state/constants';
import { 
  timeStringToMinutes, 
  removeItemByValueImmutable,
  convertLunarRangeToSolarByYear,
  convertLunarToSolar,
  calcOTP,
  safeMin, 
  safeMax,
  safeAvg,
} from 'state/helpers';
import { 
  countFlightsByField, 
  countFlightsStackedBarChart, 
  countBarLineFlightsByFieldAndDuration 
} from 'scenes/charts/utils';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import FireworksComponent from 'components/Fireworks';
import ScrollToTop from 'components/ScrollToTop';
import PageFooter from 'components/PageFooter';
import { DraggableCards } from "./draggable-cards";


const computeChartData = (flights = [], timeMode, dateDiff) => {
  const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase';

  return { 
    flightsByFlightReg: countFlightsStackedBarChart(flights, 'flightReg'), 
    dailyFlightsByFlightReg: countBarLineFlightsByFieldAndDuration(flights, 'blockHours', 'flightReg', dateDiff),
    flightsByACType: countFlightsByField(flights, 'acType', 'pie'),
    flightsByFlightDate: countFlightsStackedBarChart(flights, flightDate),
    flightsByDep: countFlightsStackedBarChart(flights, 'dep'),
    flightsBySector: countFlightsStackedBarChart(flights, 'sector'),
    flightsByFlightType: countFlightsByField(flights, 'flightType', 'pie'),
  };
};

const MAX_PAX_PER_FLIGHT = 500;

const EMPTY_STATS = { avg: 0, min: 0, max: 0 };
const INIT_INDICATORS = {
  aircraftCount: 0,
  flightsTotal: 0,
  flownFlightsCount: 0,
  blockTime: 0,
  flownBlockTime: 0,
  arrivedFlightsCount: 0,
  bh1AC1Day: 0,
  depDelaysCount: 0,
  depOntimeCount: 0,
  depOTP: 100,
  arrDelaysCount: 0,
  arrOntimeCount: 0,
  arrOTP: 100,
  noOfPax: 0,
  loadFactor: 0,
  aircraft1day: EMPTY_STATS,
  flights1day: EMPTY_STATS,
  blockHours1flight: EMPTY_STATS,
  pax1flight: EMPTY_STATS,
  depOTP1day: EMPTY_STATS,
  arrOTP1day: EMPTY_STATS,
  depDelaysTime: EMPTY_STATS,
  arrDelaysTime: EMPTY_STATS,
  scheduleTat: EMPTY_STATS,
  tat: EMPTY_STATS,
  taxiOut: EMPTY_STATS,
  taxiIn: EMPTY_STATS,
};
const updateOTP = (otp, f) => {
  if (!DIVERT_RETURN_STATUS.includes(f.flightStatus)) {
    if (f.flightStatus !== 'Not yet departed') {
      otp.dep.flights++;
      if (f.depDelaysTime > OTP_DELAY_MINUTES) 
        otp.dep.delays++
      else 
        otp.dep.ontime++;
    }

    if (f.flightStatus === 'Arrived') {
      otp.arr.flights++;
      if (f.arrDelaysTime > OTP_DELAY_MINUTES) 
        otp.arr.delays++
      else 
        otp.arr.ontime++;
    }
  }
};

const calcIndicators = (flights = [], timeMode, dateDiff) => {
  let depFlightsTotal = 0;
  let blockTimeTotal = 0;
  let departedBlockTime = 0;
  let paxCount = 0;
  let paxCapacity = 0;
  const pax1flightList = [];

  const aircraftSet = new Set();
  const aircraftPerDayMap = new Map();
  const flightsPerDayMap = new Map();
  const otp = {
    dep: { flights: 0, delays: 0, ontime: 0 },
    arr: { flights: 0, delays: 0, ontime: 0 },
  };
  const otpByDayMap = new Map();
  const depDelaysTimeList = [];
  const arrDelaysTimeList = [];
  const blockHours1flightList = [];
  const scheduleTatList = [];
  const tatList = [];
  const taxiOutList = [];
  const taxiInList = [];

  const dateKeyField = timeMode === 'UTC'
    ? 'flightDate'
    : 'flightDateLocalBase';

  flights?.forEach(f => {        
    /* --------- AIRCRAFT ------- */
    if (f.flightReg) {
      aircraftSet.add(f.flightReg);
    }
    
    /* -------- PER DAY MAPS -------- */
    const dateKey = f[dateKeyField];
    if (dateKey) {
      flightsPerDayMap.set(
        dateKey,
        (flightsPerDayMap.get(dateKey) || 0) + 1
      );

      if (!aircraftPerDayMap.has(dateKey)) {
        aircraftPerDayMap.set(dateKey, new Set());
      } else if (f.flightReg) {
        aircraftPerDayMap.get(dateKey).add(f.flightReg);
      }

      if (!otpByDayMap.has(dateKey)) {
        otpByDayMap.set(dateKey, {
          dep: { flights: 0, delays: 0, ontime: 0 },
          arr: { flights: 0, delays: 0, ontime: 0 },
        });
      }

      updateOTP(otpByDayMap.get(dateKey), f);
    }

    /* -------- TOTAL OTP ------- */
    updateOTP(otp, f);


    /* ---------- PAX ----------- */    
    const capacity = Math.min(f?.capacity || 0, MAX_PAX_PER_FLIGHT);
    const pax = Math.min(f?.noOfPax, capacity);
    if (pax > 0) {
      paxCount += Math.min(pax, capacity);
      paxCapacity += capacity;
      pax1flightList.push(pax);
    }

    /* ------- BLOCK TIME ------- */
    if (!DIVERT_RETURN_STATUS.includes(f.flightStatus)) {
      depFlightsTotal++;
      blockTimeTotal += timeStringToMinutes(f.blockTime);

      if (f.flightStatus !== 'Not yet departed') {
        departedBlockTime += timeStringToMinutes(f.blockTime);
      }
    }

    if (f.blockHours > 0) {
      blockHours1flightList.push(f.blockHours);
    }
    if (f.depDelaysTime > 0 && f.depDelaysTime > OTP_DELAY_MINUTES) {
      depDelaysTimeList.push(f.depDelaysTime);
    }
    if (f.arrDelaysTime > 0 && f.arrDelaysTime > OTP_DELAY_MINUTES) {
      arrDelaysTimeList.push(f.arrDelaysTime);
    }
    if (f.scheduleTat > 0 && f.scheduleTat < MAXIMUM_TAT_MINUTES) {
      scheduleTatList.push(f.scheduleTat);
    }
    if (f.tat > 0 && f.tat < MAXIMUM_TAT_MINUTES) {
      tatList.push(f.tat);
    }
    if (f.taxiOut > 0) {
      taxiOutList.push(f.taxiOut);
    }
    if (f.taxiIn > 0) {
      taxiInList.push(f.taxiIn);
    }
  });
  const flights1dayList = Array.from(flightsPerDayMap.values());  
  const depOTP1dayList = [];
  const arrOTP1dayList = [];

  otpByDayMap.forEach(day => {
    depOTP1dayList.push(calcOTP(day.dep.flights, day.dep.delays));
    arrOTP1dayList.push(calcOTP(day.arr.flights, day.arr.delays));
  });
  const depOTP = calcOTP(otp.dep.flights, otp.dep.delays);
  const arrOTP = calcOTP(otp.arr.flights, otp.arr.delays);

  const aircraftCount = aircraftSet.size;
  const aircraft1dayList = Array.from(aircraftPerDayMap.values()).map(
    set => set.size
  );
  const bh1AC1Day = (aircraftCount > 0 && dateDiff > 0) ? (blockTimeTotal / 60 / aircraftCount / dateDiff) : 0;
  

  return {
    aircraftCount,
    flightsTotal: depFlightsTotal,
    flownFlightsCount: otp.dep.flights,
    arrivedFlightsCount: otp.arr.flights,
    blockTime: blockTimeTotal / 60,
    flownBlockTime: departedBlockTime / 60,
    bh1AC1Day,

    depDelaysCount: otp.dep.delays,
    depOntimeCount: otp.dep.ontime,
    depOTP,
    arrDelaysCount: otp.arr.delays,
    arrOntimeCount: otp.arr.ontime,
    arrOTP,

    noOfPax: paxCount,
    loadFactor: paxCapacity && paxCount/paxCapacity,
    aircraft1day: {
      avg: safeAvg(aircraft1dayList),
      min: safeMin(aircraft1dayList),
      max: safeMax(aircraft1dayList),
    },
    flights1day: {
      avg: safeAvg(flights1dayList),
      min: safeMin(flights1dayList),
      max: safeMax(flights1dayList),
    },    
    blockHours1flight: {
      avg: safeAvg(blockHours1flightList),
      min: safeMin(blockHours1flightList),
      max: safeMax(blockHours1flightList),
    },
    pax1flight: {
      avg: safeAvg(pax1flightList),
      min: safeMin(pax1flightList),
      max: safeMax(pax1flightList),
    },    
    depOTP1day: {
      avg: depOTP,
      min: safeMin(depOTP1dayList),
      max: safeMax(depOTP1dayList),
    },
    arrOTP1day: {
      avg: arrOTP,
      min: safeMin(arrOTP1dayList),
      max: safeMax(arrOTP1dayList),
    },
    depDelaysTime: {
      avg: safeAvg(depDelaysTimeList),
      min: safeMin(depDelaysTimeList),
      max: safeMax(depDelaysTimeList),
    },
    arrDelaysTime: {
      avg: safeAvg(arrDelaysTimeList),
      min: safeMin(arrDelaysTimeList),
      max: safeMax(arrDelaysTimeList),
    },
    scheduleTat: {
      avg: safeAvg(scheduleTatList),
      min: safeMin(scheduleTatList),
      max: safeMax(scheduleTatList),
    },
    tat: {
      avg: safeAvg(tatList),
      min: safeMin(tatList),
      max: safeMax(tatList),
    },    
    taxiOut: {
      avg: safeAvg(taxiOutList),
      min: safeMin(taxiOutList),
      max: safeMax(taxiOutList),
    },
    taxiIn: {
      avg: safeAvg(taxiInList),
      min: safeMin(taxiInList),
      max: safeMax(taxiInList),
    },
  };
};


const HomePage = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { startDate, endDate } = useDateRangeContext();
  const dateDiff = differenceInDays(endDate, startDate) + 1;
  
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);
  const [holidays, setHolidays] = useState([]);
  
  const flightsData = useSelector((state) => state.flights); 
  const crewRoutesData = useSelector((state) => state.crewRoutes); 
  const [crewPairings, setCrewPairings] = useState([]);

  const [cards, setCards] = useState(() => {
    const REQUIRED_IDS = [
      "indicators", 
      "charts", 
      "delays", 
      "otpByPorts", 
      "crewtats"
    ];
    
    try {
      const saved = localStorage.getItem('mainPageCardOrder');
      const savedIds = saved ? JSON.parse(saved) : null;
      const isValid = Array.isArray(savedIds) &&
        REQUIRED_IDS.every(id => savedIds.includes(id)) &&
        savedIds.every(id => REQUIRED_IDS.includes(id) || id === "indexes");
      const orderedIds = isValid ? savedIds : REQUIRED_IDS;
      return orderedIds.map(id => ({
        id,
        type: id,
        data: id === "indicators" || id === "indexes" ? INIT_INDICATORS : [],
      }));
    } catch {
      return REQUIRED_IDS.map(id => ({ id, type: id, data: id === "indicators" ? INIT_INDICATORS : [] }));
    }
  });
    

  const fetchHolidays = useCallback(async () => {
    const response = await api.get('/holidays/active');
    setHolidays(response.data || []);
  }, []);

  const fetchCrewPairings = useCallback(async () => {
    if (!startDate || !endDate) return;

    const url = '/flights/crewpairings';     
    const payload = { 
      startDate: addDays(startDate, -1), 
      endDate: addDays(endDate, 1) 
    };      
    const response = await api.post(url, payload);

    if (response.data) {
      const filteredRoutes = response.data?.filter(route => {
        const routeDateToCompare = timeMode === 'UTC'
          ? route.routeDate
          : route.routeDateLocalBase;

        return (
          compareAsc(routeDateToCompare, startDate) >= 0 &&
          compareAsc(routeDateToCompare, endDate) <= 0
        );
      });

      setCrewPairings(filteredRoutes);
    }
  }, [timeMode, startDate, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        await Promise.all([
          fetchHolidays(),
          fetchCrewPairings(),
        ]);                
        
      } catch (error) {
        const message = error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch data!";

        toast.error(message);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [      
    fetchHolidays,
    fetchCrewPairings,  
  ]);
  
  const isHolidayActiveToday = useMemo(() => {
    if (!holidays.length) return false;

    const today = new Date();
    const currentYear = today.getFullYear();

    return holidays.some(holiday => {
      if (!holiday.isActive) return false; // Skip inactive holidays
      
      let start = new Date(holiday.startDate);
      let end = holiday.endDate ? new Date(holiday.endDate) : start; // Default end to start if not provided

      // ===== LUNAR RANGE =====
      if (holiday.isLunar) {
        if (holiday.frequency === "yearly") {
          // yearly single day
          const solar = convertLunarToSolar(
            `${currentYear}-${format(start, "MM")}-${format(start, "dd")}`
          );
          return solar && isSameDay(today, solar);
        }

        // lunar range (once / yearly range)
        const range = convertLunarRangeToSolarByYear(
          start,
          end,
          currentYear
        );

        if (!range) return false;

        return today >= range.solarStart && today <= range.solarEnd;
      }
      
      // ===== SOLAR NORMAL =====
      switch (holiday.frequency) {
        case 'once':
          return isSameDay(today, start) || (start <= today && end >= today);
        case 'weekly':
          return today.getDay() === start.getDay(); // Check if today is the same day of week
        case 'monthly':
          return today.getDate() === start.getDate(); // Check if today is the same day of month
        case 'yearly':
          return today.getMonth() === start.getMonth() && today.getDate() === start.getDate(); // Check if same month and day
        default:
          return false;
      }
    });
  }, [holidays]);

  // Trigger fireworks based on holiday check
  useEffect(() => {
    if (loading) return; // Wait until loading is complete

    if (isHolidayActiveToday) {
      setShowFireworks(true);

      const timeout = setTimeout(() => {
        setShowFireworks(false);
      }, 9000);

      return () => clearTimeout(timeout); 
    } else {
      setShowFireworks(false); // Ensure fireworks are off if not a holiday
    }
  }, [isHolidayActiveToday, loading]);

  const filteredFlights = useMemo(() => removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS), [flightsData]);
  const nonDivFlights = useMemo(() => filteredFlights.filter(flight => !DIVERT_RETURN_STATUS.includes(flight.flightStatus)), [filteredFlights]);

  const computeCards = useCallback(() => {
    try {
      setLoading(true);
      const chartData = computeChartData(nonDivFlights, timeMode, dateDiff);
      const newIndicators = calcIndicators(nonDivFlights, timeMode, dateDiff);

      setCards(prevCards =>
        prevCards.map(card => {
          if (card.id === "indicators") return { ...card, data: newIndicators };
          if (card.id === "indexes")    return { ...card, data: newIndicators };
          if (card.id === "charts") return { ...card, data: chartData };
          if (card.id === "delays") return { ...card, data: filteredFlights };
          if (card.id === "otpByPorts") return { ...card, data: nonDivFlights };
          if (card.id === "crewtats") return { ...card, data: crewPairings || crewRoutesData };
          return card;
        })
      );
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to compute flights data!");
    } finally {
      setLoading(false);
    }
  }, [
    timeMode, 
    filteredFlights, 
    nonDivFlights, 
    crewPairings, 
    crewRoutesData, 
    dateDiff
  ]);

  useEffect(() => {
    const timer = setTimeout(computeCards, 500);
    return () => clearTimeout(timer);
  }, [computeCards]);
    
  const handleToggleIndexes = useCallback(() => {
    setCards(prev => {
      let newCards;
      if (prev.some(c => c.id === "indexes")) {
        const pos = prev.findIndex(c => c.id === "indexes");
        localStorage.setItem('mainPageIndexesPosition', pos.toString());
        newCards = prev.filter(c => c.id !== "indexes");
      } else {
        const savedPos = parseInt(localStorage.getItem('mainPageIndexesPosition') ?? '0', 10);
        const insertAt = Math.min(Math.max(savedPos, 0), prev.length);
        const indicatorsData = prev.find(c => c.id === "indicators")?.data ?? INIT_INDICATORS;
        newCards = [
          ...prev.slice(0, insertAt),
          { id: "indexes", type: "indexes", data: indicatorsData },
          ...prev.slice(insertAt),
        ];
      }
      localStorage.setItem('mainPageCardOrder', JSON.stringify(newCards.map(c => c.id)));
      return newCards;
    });
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setCards((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('mainPageCardOrder', JSON.stringify(newItems.map(c => c.id)));
        return newItems;
      });
    }
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Main"
          subtitle={loading ? 'Loading...' : 'Welcome to the main page'}
        />
        <StyledButton
          // startIcon={<Adjust />}
          onClick={handleToggleIndexes}
          sx={cards.some(c => c.id === "indexes")
            ? { color: "primary.main" }
            : {}
          }
        >
          <DragIndicator />
        </StyledButton>
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || 'An unexpected error occurred.'}</Typography>
        </FlexCenter>
      ) : (        
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ overflowY: 'auto', pr: !isMobile ? 0.5 : 0 }}
        >            
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={cards.map((card) => card.id)} 
              strategy={verticalListSortingStrategy}
            >
              <DraggableCards cards={cards} />
            </SortableContext>
          </DndContext>
          
          <PageFooter />           
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}    

      {showFireworks && <FireworksComponent />}         
    </Box>
  );
};

export default HomePage;
